import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Notation from "../../../components/report/notation";
import ReportTable from "../../../components/report/report-table";
import ScoreView from "../../../components/UI/ScoreView";
import {
  ClassReportSubTopicWiseService,
  OverallClassReportService,
} from "../../../services/reports";
import * as actions from "../../../store/actions";
import * as configActions from "../../../store/config/actions";
import { handleCompPerformance } from "../../../helpers";
import { textIdentifier } from "../../../textIdentifier";


export default function OverallClassReport() {
  const { selectedCompetition, selectedSchool, selectedClass,lang } = useSelector(
    (state) => state.main
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [values, setValues] = useState({});
  const [isFetching, setIsFetching] = useState(true);

  const fetchReportsData = async () => {
    try {
      const res = await OverallClassReportService(
        selectedCompetition.id,
        selectedClass.code
      );

      // subTopic API
      const classSubTopics = await ClassReportSubTopicWiseService(
        selectedCompetition.id,
        selectedClass.code
      );

      //  filtering data
      const { accuracy, proficiency, marks } = res.data;
      let class_proficiency_status = handleCompPerformance(
        proficiency,
        selectedSchool.proficiency
      );
      let school_proficiency_status = handleCompPerformance(
        selectedSchool.proficiency,
        selectedCompetition.proficiency
      );
      let class_accuracy_status = handleCompPerformance(
        accuracy,
        selectedSchool.accuracy
      );
      let school_accuracy_status = handleCompPerformance(
        selectedSchool.accuracy,
        selectedCompetition.accuracy
      );
      let class_marks_status = handleCompPerformance(
        marks,
        selectedSchool.marks
      );
      let school_marks_status = handleCompPerformance(
        selectedSchool.marks,
        selectedCompetition.marks
      );
      const val = Object.assign(res.data, {
        class_proficiency_status,
        school_proficiency_status,
        class_accuracy_status,
        school_accuracy_status,
        class_marks_status,
        school_marks_status,
      });
      setValues(val);
      dispatch(
        actions.selectClass(
          Object.assign(selectedClass, {
            percentile: res.data.percentile,
            marks: res.data.marks,
            accuracy: res.data.accuracy,
            proficiency: res.data.proficiency,
            subTopicData: classSubTopics.data,
            hide_skills: res.data.hide_skills
          })
        )
      );
    } catch (error) {
      dispatch(configActions.showAlert(error?.data?.message));
    } finally {
      setIsFetching(false);
    }
  };

  const handleFloatParse = (val) => {
    return parseFloat(val).toFixed(2);
  };

  const competition_proficiency_status = (
    <ScoreView
      content={textIdentifier["overall-proficiency"][lang]+` ${parseFloat(
        selectedCompetition.proficiency
      ).toFixed(2)}%`}
    >
      <div>
        <Notation status={values.school_proficiency_status} />
      </div>
    </ScoreView>
  );

  const school_proficiency_status = (
    <ScoreView
      content={textIdentifier["school-proficiency"][lang]+` ${parseFloat(
        selectedSchool.proficiency
      ).toFixed(2)}%`}
    >
      <div>
        <Notation status={values.class_proficiency_status} />
      </div>
    </ScoreView>
  );

  const competition_accuracy_status = (
    <ScoreView
      content={textIdentifier["overall-accuracy"][lang]+` ${parseFloat(
        selectedCompetition.accuracy
      ).toFixed(2)}%`}
    >
      <div>
        <Notation status={values.school_accuracy_status} />
      </div>
    </ScoreView>
  );

  const school_accuracy_status = (
    <ScoreView
      content={textIdentifier["school-accuracy"][lang]+` ${parseFloat(selectedSchool.accuracy).toFixed(
        2
      )}%`}
    >
      <div>
        <Notation status={values.class_accuracy_status} />
      </div>
    </ScoreView>
  );

  const competition_marks_status = (
    <ScoreView
      content={textIdentifier["overall-marks"][lang]+` ${parseFloat(selectedCompetition.marks).toFixed(
        2
      )}`}
    >
      <div>
        <Notation status={values.school_proficiency_status} />
      </div>
    </ScoreView>
  );

  const school_marks_status = (
    <ScoreView
      content={textIdentifier["school-marks"][lang]+` ${parseFloat(selectedSchool.marks).toFixed(2)}`}
    >
      <div>
        <Notation status={values.class_proficiency_status} />
      </div>
    </ScoreView>
  );

  useEffect(() => {
    if (!selectedCompetition || !selectedSchool) {
      navigate("/");
    } else {
      fetchReportsData();
    }
  }, []);

  if (isFetching) {
    return <div className="cr-card"></div>;
  }

  if (!values && !isFetching) {
    return <p>{textIdentifier["no-data-found"][lang]}</p>;
  }

  return (
    <div className="cr-card">
      <ReportTable
        data={[
          [
            `${parseFloat(values.proficiency).toFixed(2)}%`,
            `${parseFloat(values.accuracy).toFixed(2)}%`,
            handleFloatParse(
              values.easy_question_count +
                values.medium_question_count +
                values.hard_question_count
            ),
            handleFloatParse(values.marks),
          ],
          [
            <div className="flex-center">
              {competition_proficiency_status}
              {school_proficiency_status}
            </div>,
            <div className="flex-center">
              {competition_accuracy_status}
              {school_accuracy_status}
            </div>,
            `(E${handleFloatParse(
              values.easy_question_count
            )}, M${handleFloatParse(
              values.medium_question_count
            )}, H${handleFloatParse(values.hard_question_count)})`,
            <div className="flex-center">
              {competition_marks_status}
              {school_marks_status}
            </div>,
          ],
        ]}
        headers={[
          [
            textIdentifier["class-school-rank"][lang]+`: ${values.class_rank_in_school}/${values.classes_in_school}`,
            textIdentifier["class-school-percentile"][lang]+`: ${values.percentile}%`,
            textIdentifier["class-overall-rank"][lang]+`: ${values.class_rank_in_competition}/${values.classes_in_competition}`,
            textIdentifier["class-overall-percentile"][lang]+`: ${values.class_percentile_in_competition}%`,
          ],
          [
            textIdentifier["proficiency"][lang],
            textIdentifier["accuracy"][lang],
            textIdentifier["average-question-count"][lang],
            textIdentifier["average-marks"][lang],
          ],
        ]}
      />
    </div>
  );
}
